import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { ReuseTabService } from '@delon/abc/reuse-tab';
import { _HttpClient, SettingsService } from '@delon/theme';
import { NzSelectComponent } from 'ng-zorro-antd/select';

import { AreaAddConfigService } from '../../inline-add-panel/area-add-config.service';
import { SelectBaseComponent } from '../select-base';
import { SelectInstantBaseNewComponent } from '../selectInstant-base-new';

@Component({
  selector: 'app-area-select',
  templateUrl: './area-select.component.html',
  styles: [
    `
      /* ===== CONTAINER STYLES ===== */
      .area-select-container {
        position: relative;
        width: 100%;
      }

      /* ===== ADD NEW BUTTON STYLES ===== */
      ::ng-deep .add-new-button {
        color: #000000 !important;
        font-weight: 500 !important;
        width: 100% !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 2px !important;
        background-color: transparent !important;
        border: none !important;
      }

      ::ng-deep .add-new-button .anticon {
        color: #000000 !important;
      }

      /* ===== OCR SUGGESTED ITEMS ===== */
      ::ng-deep .ocr-suggested-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 0;
        cursor: pointer;
        width: 100%;
      }

      ::ng-deep .ocr-text {
        font-weight: 500;
        flex: 1;
      }

      /* ===== ULTRA COMPACT DROPDOWN STYLES ===== */
      ::ng-deep .ant-select-dropdown:has(app-area-select-item) {
        max-height: none !important;
        overflow: hidden !important;
        width: 300px !important;
      }

      ::ng-deep
        .ant-select-dropdown:has(app-area-select-item)
        .ant-select-dropdown-menu {
        max-height: 240px !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
      }

      ::ng-deep
        .ant-select-dropdown:has(app-area-select-item)
        .ant-select-item-option {
        height: auto !important;
        min-height: 60px !important;
        padding: 6px 10px !important;
      }

      /* ===== AREA ITEM LAYOUT ===== */
      ::ng-deep .ant-select-dropdown app-area-select-item {
        display: block !important;
        width: 100% !important;
        height: auto !important;
      }

      ::ng-deep .ant-select-dropdown app-area-select-item .width-lg {
        display: block !important;
        width: 100% !important;
      }

      ::ng-deep
        .ant-select-dropdown
        app-area-select-item
        .width-lg
        > div:first-child {
        display: flex !important;
        justify-content: space-between !important;
        align-items: center !important;
        width: 100% !important;
      }

      ::ng-deep .ant-select-dropdown app-area-select-item nz-tag {
        display: inline-block !important;
        visibility: visible !important;
        opacity: 1 !important;
        margin-left: auto !important;
      }

      /* ===== SPECIAL OPTION HIGHLIGHTING ===== */
      ::ng-deep .ant-select-item-option-content .special-option {
        text-align: center !important;
      }

      /* ===== YELLOW HIGHLIGHT FOR SECOND ITEM ===== */
      ::ng-deep
        .dropdownSelect:not(:has(.ant-select-item-option-active))
        .ant-select-item-option:nth-child(2):not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover),
      ::ng-deep
        .dropdownSelect
        .ant-select-item-option:first-child:last-child:not(
          .ant-select-item-option-active
        ):not(.ant-select-item-option-selected):not(:hover) {
        background-color: #ffc000;
      }
    `
  ],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AreaSelectComponent),
      multi: true
    }
  ]
})
export class AreaSelectComponent
  extends SelectInstantBaseNewComponent
  implements OnInit, AfterViewInit
{
  @ViewChild('selector') nzSelectComponent!: NzSelectComponent;

  @Input() type: 'customer' | 'supplier' = 'customer';
  @Input() activeOnly = true;
  @Input() selectDisabled: boolean = false;
  @Input() tabindex: string | null = null;
  @Input() labelField: 'name' | 'full' | null = null;
  @Input() nzAllowClear: boolean = true;

  @Output() readonly addNewArea = new EventEmitter<any>();

  NotShowInReport: boolean = false;
  areaConfig: any;
  inlineDefaults: any = null;
  // Store instant data separately to preserve it when dropdown closes

  constructor(
    http: _HttpClient,
    router: Router,
    cdr: ChangeDetectorRef,
    private resueTabService: ReuseTabService,
    private areaAddConfigService: AreaAddConfigService,
    private settingsService: SettingsService
  ) {
    super(http, cdr, router);
  }

  ngOnInit(): void {
    this.checkAddNewOptionInReportAccess();
    this.areaConfig = this.areaAddConfigService.getAreaConfig();

    // Listen to search form changes to store instant data
    this.searchForm.controls.keyword.valueChanges.subscribe(value => {
      if (value) {
        const trimmedValue = value.trim();
        this.storedInstantData = trimmedValue;
        // Also store in pendingInstantData for Add New (handled by base class)
        this.pendingInstantData = trimmedValue;
      }
    });

    // Ensure the correct item is active whenever the list updates while open
    this.onListFilled.subscribe(() => {
      if ((this.nzSelectComponent as any)?.nzOpen) {
        this.ensureFirstItemActivatedAndFocused();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.tabindex) {
      const nzSelectElement =
        this.nzSelectComponent.originElement.nativeElement;
      const searchInput = nzSelectElement.querySelector('input');

      if (searchInput) {
        searchInput.setAttribute('tabindex', this.tabindex?.toString() || '0');

        // Listen to input events directly
        searchInput.addEventListener('input', (event: any) => {
          if (event.target.value) {
            const trimmedValue = event.target.value.trim();
            this.storedInstantData = trimmedValue;
            // Also store in pendingInstantData for Add New (handled by base class)
            this.pendingInstantData = trimmedValue;
          }
        });
      }
    }
  }

  getLabel(item: any) {
    if (item) {
      if (this.labelField === 'name') return item.name;
      else if (this.labelField === 'full') return `${item.code} - ${item.name}`;
      return item.code;
    }
    return '';
  }

  getUrl(searchValue: string): string {
    const excludeType =
      this.type === 'customer' ? `'SupplierOnly'` : `'CustomerOnly'`;
    let filter = `type ne ${excludeType}`;
    if (this.activeOnly) {
      filter += ` and isActive eq true`;
    }

    let isValueFieldMatch = false;
    if (searchValue && searchValue.startsWith(SelectBaseComponent.VALUEFIELD_MATCH_PREFIX)) {
      searchValue = searchValue.substring(SelectBaseComponent.VALUEFIELD_MATCH_PREFIX.length);
      isValueFieldMatch = true;
    }

    if (searchValue) {
      // Ensure searchValue is a string before calling replaceAll
      const searchStr = String(searchValue).replaceAll("'", "''");

      // Check if searchValue can be converted to int (ID)
      const searchValueNum = parseInt(searchValue, 10);
      const isNumericId =
        !isNaN(searchValueNum) &&
        searchValueNum.toString() === searchValue.trim();

      if (isValueFieldMatch) {
        // ValueField match: use eq operator for precise matching based on valueField
        // Ignore prerequisite conditions (like isActive) when matching by valueField
        // This ensures we can find records even if they are inactive
        let filter: string;
        if (isNumericId) {
          // For numeric values, match by ID exactly
          filter = `id eq ${searchValueNum}`;
        } else if (this.valueField === 'code') {
          // For non-numeric values when valueField is 'code', match by code exactly
          filter = `code eq '${encodeURIComponent(searchStr)}'`;
        } else {
          // For other valueFields, match by the specified field exactly
          filter = `${this.valueField} eq '${encodeURIComponent(searchStr)}'`;
        }
        return `api/areas/query?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
      } else {
        // Partial match: use contains for search behavior with prerequisite conditions
        let filter = `type ne ${excludeType}`;
        if (this.activeOnly) {
          filter += ` and isActive eq true`;
        }
        if (isNumericId) {
          // Include ID matching: (Id eq <id>) or (code/name contains search)
          filter = `(${filter}) and ((id eq ${searchValueNum}) or (contains(code, '${encodeURIComponent(searchStr)}') or contains(name,'${encodeURIComponent(searchStr)}')))`;
        } else {
          // Original behavior: only search by code/name
          filter = `(${filter}) and (contains(code, '${encodeURIComponent(
            searchStr
          )}') or contains(name,'${encodeURIComponent(searchStr)}'))`;
        }
        return `api/areas/query?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
      }
    } else {
      // No search value: apply prerequisite conditions
      let filter = `type ne ${excludeType}`;
      if (this.activeOnly) {
        filter += ` and isActive eq true`;
      }
      return `api/areas/query?$top=50&$skip=0&$orderby=code asc&$filter=${filter}`;
    }
  }

  // set the location want to navigate
  jump(): string {
    return '/company/area';
  }

  // set the param
  queryParam(): String {
    return 'AreadCode';
  }

  checkAddNewOptionInReportAccess(): boolean {
    let curUrl = this.resueTabService.curUrl.toLowerCase();

    if (curUrl.includes('report')) {
      this.NotShowInReport = true;
    } else {
      this.NotShowInReport = false;
    }

    return this.NotShowInReport;
  }
  override async writeValue(value: any) {
    // Extract ID if value is an object (e.g., from API response)
    let idValue = value;
    if (value && typeof value === 'object' && this.valueField) {
      idValue = value[this.valueField] || value.id;
    }

    this.value = idValue;

    // If use selected item as value
    if (this.value) {
      if (!this.valueField) {
        this.ensureItemPresentList(value);
        this.selected = value;
      } else {
        // When valueField is set, we need to fetch the list to find the matching item
        // Use valueField match to find the record that exactly matches the valueField (ID or Code)
        // Prefix keyword with special marker to indicate valueField match (ignores prerequisite conditions)
        let keyword = `${SelectBaseComponent.VALUEFIELD_MATCH_PREFIX}${idValue}`;
        await this.fetchData(keyword);
        this.selected = this.list.find(x => x[this.valueField] == idValue);
      }
    } else {
      this.selected = null;
      //this.list = [];
    }
  }

  // Override the addNew method to show inline panel instead of navigation
  override addNew(): void {
    // Use pendingInstantData which was captured synchronously in base class onAddNewClick
    // This ensures we have the data even if storedInstantData was cleared
    const instantDataToUse = this.pendingInstantData || this.storedInstantData;

    // If we still don't have data, try to capture it
    if (!instantDataToUse) {
      this.captureCurrentSearchValue();
      const currentSearchValue = this.getCurrentSearchValue();
      if (currentSearchValue) {
        this.pendingInstantData = currentSearchValue;
      }
    }

    // Final fallback - use whatever we have
    const finalInstantData =
      this.pendingInstantData || this.storedInstantData || '';

    // Load options and defaults first, then show panel
    Promise.all([this.loadSelectOptions(), this.fetchInlineDefaults()]).then(
      () => {
        // Merge instant data into initialData as fallback
        if (finalInstantData) {
          this.inlineDefaults = {
            ...this.inlineDefaults,
            code: finalInstantData
          };
        }

        this.showInlineAddPanel = true;
        // Force change detection to ensure the panel is rendered
        this.cdr.detectChanges();

        // Retry mechanism to ensure changeInstantData is called after form is ready
        const trySetInstantData = (retries: number = 10) => {
          if (!finalInstantData) return;

          if (this.inlineAddPanel && this.inlineAddPanel.formGroup) {
            const instantFieldName =
              this.areaConfig?.fields?.find((f: any) => f.isInstantData)
                ?.name || 'code';
            const control = this.inlineAddPanel.formGroup.get(instantFieldName);
            if (control) {
              this.inlineAddPanel.changeInstantData(finalInstantData);
              return;
            }
          }

          if (retries > 0) {
            setTimeout(() => trySetInstantData(retries - 1), 50);
          }
        };

        // Try immediately and with retries
        setTimeout(() => {
          if (this.inlineAddPanel && finalInstantData) {
            this.inlineAddPanel.changeInstantData(finalInstantData);
          }
          trySetInstantData();
        }, 0);

        if (this.nzSelectComponent) {
          this.nzSelectComponent.setOpenState(false);
        }
      }
    );
  }

  onAreaSaved(areaData: any): void {
    // Create the area object in the expected format
    const area = {
      id: areaData.id,
      code: areaData.code,
      name: areaData.name,
      type: areaData.type,
      isActive: areaData.isActive,
      isDefault: areaData.isDefault
    };

    // Add the new area to the list and select it
    this.list.unshift(area);

    // Set the area object in the form control to trigger area.valueChanges
    this.value = area.id;
    this.selected = area.id;

    // Set the area object directly in the form control
    this.onChange(area);
    this.selectedChange.emit(area);
    this.showInlineAddPanel = false;
  }

  onAreaCreated(event: { item: any; selectionConfig?: any }): void {
    if (event.item && event.selectionConfig) {
      // Create the area object in the expected format
      const area = {
        id: event.item[event.selectionConfig.valueField],
        code: event.item.code,
        name: event.item[event.selectionConfig.labelField],
        type: event.item.type,
        isActive: event.item.isActive,
        isDefault: event.item.isDefault
      };

      // Add the new area to the list and select it
      this.list.unshift(area);

      // Close the inline panel first
      this.showInlineAddPanel = false;

      // Set the area object in the form control to trigger area.valueChanges
      this.value = area.id;
      this.selected = area.id;

      // Set the area object directly in the form control
      this.onChange(area);
      this.writeValue(area);
      // Emit addNewArea event for parent component to handle
      this.addNewArea.emit(area);

      // Clear stored instant data after successful creation
      this.clearStoredInstantData();

      // Force change detection
      this.cdr.detectChanges();
    }
  }

  onAreaCancelled(): void {
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onAreaMoreDetails(): void {
    // Navigate to area details page with all the current form data
    const formData = this.inlineAddPanel?.formGroup?.value;
    if (formData) {
      // Create query params with all form fields
      const queryParams: any = {
        autoPopulate: 1
      };

      // Add all form fields to query params
      if (formData.name) queryParams.AreaName = formData.name;
      if (formData.code) queryParams.AreaCode = formData.code;
      if (formData.type !== undefined) queryParams.Type = formData.type;
      if (formData.isDefault !== undefined)
        queryParams.IsDefault = formData.isDefault;

      queryParams.autoPopulate = 1;

      this.router.navigate(['/company/area'], {
        queryParams: queryParams
      });
    } else {
      this.router.navigate(['/company/area'], {
        queryParams: {
          autoPopulate: 1
        }
      });
    }
    this.showInlineAddPanel = false;
    this.clearStoredInstantData();
  }

  onFieldChange(event: { fieldName: string; value: any }): void {
    // Handle field changes - auto-code component will handle code generation
  }

  private loadSelectOptions(): Promise<void> {
    // Load options for area type
    return Promise.all([this.loadAreaTypeOptions()]).then(() => {
      // Force change detection after options are loaded
      this.cdr.detectChanges();

      // Additional change detection for the inline panel
      if (this.inlineAddPanel) {
        // Force change detection on the inline panel
        setTimeout(() => {
          this.cdr.detectChanges();
        }, 100);
      }
    });
  }

  private loadAreaTypeOptions(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Area type options are already defined in the config
      const areaTypeField = this.areaConfig.fields.find(
        (f: any) => f.name === 'type'
      );
      if (areaTypeField) {
        // Base options
        const allOptions = [
          { label: 'Both', value: 0 },
          { label: 'Customer Only', value: 1 },
          { label: 'Supplier Only', value: 2 }
        ];

        // Filter options based on component type
        let filteredOptions = allOptions;
        if (this.type === 'customer') {
          // Hide 'Supplier Only' option for customer type
          filteredOptions = allOptions.filter(option => option.value !== 2);
        } else if (this.type === 'supplier') {
          // Hide 'Customer Only' option for supplier type
          filteredOptions = allOptions.filter(option => option.value !== 1);
        }

        areaTypeField.options = filteredOptions;
      }
      resolve();
    });
  }

  private fetchInlineDefaults(): Promise<void> {
    // Call server to get defaults similar to area/new
    return new Promise(resolve => {
      this.http.get('api/areas/new').subscribe({
        next: (data: any) => {
          const defaults: any = {};
          // Always set type to "Both" (value 0) when opening inline add panel
          defaults.type = 0;
          if (data?.isActive !== undefined) defaults.isActive = data.isActive;
          if (data?.isDefault !== undefined)
            defaults.isDefault = data.isDefault;
          this.inlineDefaults = defaults;
          resolve();
        },
        error: () => {
          // Even if server call fails, set type to "Both" as default
          this.inlineDefaults = { type: 0 };
          resolve();
        }
      });
    });
  }
}
